import { css, html, LitElement, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";

import { getAssetUrl } from "helpers/assetHelpers";
import { getMediaMimeTypeFromUrl } from "helpers/getMediaMimeTypeFromUrl";

import "../button/wow-button";

import "@phosphor-icons/webcomponents/PhPlay";
import "@phosphor-icons/webcomponents/PhSpeakerHigh";
import "@phosphor-icons/webcomponents/PhSpeakerSlash";

@customElement("image-or-video")
export class ImageOrVideo extends LitElement {
  static override styles = [
    css`
      :host {
        position: relative;
      }

      video,
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        z-index: 0;
        pointer-events: none;
      }

      .speaker-icon-button,
      .play-button {
        position: absolute;
        top: var(--sds-size-space-300);
        right: var(--sds-size-space-300);

        display: inline-flex;
        justify-content: center;
        align-items: center;

        width: var(--sds-size-icon-small);
        height: var(--sds-size-icon-small);
        border-radius: var(--sds-size-radius-full);
        border: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
        background: var(--sds-color-background-neutral-secondary);
        color: var(--sds-color-icon-default-default);
        cursor: pointer;
        appearance: none;
        outline: none;
        box-sizing: border-box;
      }

      .play-button {
        top: calc(var(--sds-size-space-1200) + var(--sds-size-space-300));
      }

      :host([for="post-modal"]) .speaker-icon-button {
        top: calc(var(--sds-size-space-2400) + var(--sds-size-space-400));
        right: var(--sds-size-space-800);
        width: var(--sds-size-icon-large);
        height: var(--sds-size-icon-large);
      }

      :host([for="post-modal"]) .play-button {
        top: var(--sds-size-space-4000);
        right: var(--sds-size-space-800);
        width: var(--sds-size-icon-large);
        height: var(--sds-size-icon-large);
      }
    `,
  ];

  @property({ type: String })
  url = "";

  @property({ type: String })
  alt?: string;

  @property({ type: Boolean })
  interactive = false;

  @property({ type: String, reflect: true })
  for: "post-modal" | "general" = "post-modal";

  @state() muted = true;
  @state() showPlayButton = false;

  @query("video") videoElement!: HTMLVideoElement;

  private handleVideoMute() {
    this.muted = !this.muted;
    if (this.videoElement) this.videoElement.muted = this.muted;
  }

  private handleCanPlay(e: Event) {
    const video = e.target as HTMLVideoElement;

    video.muted = this.muted;
    video.playsInline = true;

    if (video.paused) {
      video.play().catch((err) => {
        console.warn("Autoplay blocked:", err);
        this.showPlayButton = true;
      });
    }
  }

  private handleVideoPlay() {
    const video = this.renderRoot.querySelector("video") as HTMLVideoElement;

    if (video) {
      video.play();
      this.showPlayButton = false;
    }
  }

  private renderInteractions() {
    if (!this.interactive) return;

    return html`
      <div
        class="speaker-icon-button"
        aria-pressed="${this.muted ? "true" : "false"}"
        title="${this.muted ? "Unmute" : "Mute"}"
        @click=${this.handleVideoMute}
      >
        ${this.muted
          ? html`<ph-speaker-slash size="${this.for === "post-modal" ? "20px" : "12px"}"></ph-speaker-slash>`
          : html`<ph-speaker-high size="${this.for === "post-modal" ? "20px" : "12px"}"></ph-speaker-high>`}
      </div>

      ${this.showPlayButton
        ? html`<div class="play-button" size="small" @click=${this.handleVideoPlay}>
            <ph-play size="${this.for === "post-modal" ? "20px" : "12px"}"></ph-play>
          </div>`
        : nothing}
    `;
  }

  override render() {
    const type = getMediaMimeTypeFromUrl(this.url);

    const assetUrl = getAssetUrl(this.url);
    if (type?.kind === "image") {
      return html`<img src=${assetUrl} alt=${this.alt ?? "highlighted post image"} />`;
    }
    return html`
      <video
        autoplay
        muted
        loop
        playsinline
        preload="auto"
        src=${assetUrl}
        @canplay=${this.handleCanPlay}
        @error=${(e: Event) => console.error("Video error:", (e.target as HTMLVideoElement)?.error)}
      >
        Your browser does not support the video tag.
      </video>
      ${this.renderInteractions()}
    `;
  }
}
